/**
 * Game Store Defaults
 * 
 * Default configurations and initial state values for the game store.
 */

import { 
  GameSettings, 
  Statistics, 
  SpeedDrillState, 
  TableConfiguration, 
  TableState, 
  CountCheckState, 
  LevelingConfig,
  Seat
} from '../../types';

// Default leveling configuration
export const DEFAULT_LEVELING: LevelingConfig = {
  baseXP: 100,
  increment: 25,
  maxLevel: 50,
  stages: [
    { level: 1, title: 'Newcomer' },
    { level: 5, title: 'Beginner' },
    { level: 10, title: 'Novice' },
    { level: 15, title: 'Apprentice' },
    { level: 20, title: 'Card Counter' },
    { level: 25, title: 'Skilled Player' },
    { level: 30, title: 'Professional' },
    { level: 35, title: 'Expert Counter' },
    { level: 40, title: 'Master Counter' },
    { level: 45, title: 'Legendary Counter' },
  ],
};

// Default game settings
export const DEFAULT_SETTINGS: GameSettings = {
  numDecks: 6,
  penetration: 75,
  dealerHitsSoft17: true,
  doubleAfterSplit: true,
  surrenderAllowed: false,
  resplitAces: false,
  blackjackPays: 1.5,
  countingSystem: 'hi-lo',
  minBet: 25,
  maxBet: 500,
  shuffleAfterHand: false,
  startingBankroll: 10000,
  insuranceAllowed: true,
  earlySurrenderAllowed: false,
  useDeviations: false,
  soundEnabled: true,
  highContrast: false,
  hideHandTotals: false,
  casinoName: 'JK Casino',
  feedbackMode: 'bad-only',
  leveling: DEFAULT_LEVELING,
  customAchievements: [],
  achievementEmojiOptions: ['🎯', '🏆', '⭐', '🔥', '💎', '🚀', '🧠', '💰', '⚡', '📈', '🎰'],
  defaultSpeedDrillDuration: 60,
  defaultSpeedDrillSpeed: 1000,
  aiThinkTimeMinMs: 3000,
  aiThinkTimeMaxMs: 5000,
  aiMistakeRate: 0,
  devMode: false
};

// Default statistics
export const DEFAULT_STATISTICS: Statistics = {
  handsPlayed: 0,
  handsWon: 0,
  handsLost: 0,
  handsPushed: 0,
  blackjacks: 0,
  totalWagered: 0,
  totalWon: 0,
  countAccuracy: 0,
  countAttempts: 0,
  betSizingAccuracy: 0,
  betsPlaced: 0,
  perfectBets: 0,
  countErrors: 0,
  sessionStartTime: Date.now(),
  biggestWin: 0,
  biggestLoss: 0,
  averageResponseTime: 0,
  fastestCorrectTime: null,
  consecutiveCorrect: 0,
  bestStreak: 0,
  insuranceBetsWon: 0,
  insuranceBetsLost: 0,
  splitHandsPlayed: 0,
  currentBankroll: DEFAULT_SETTINGS.startingBankroll,
  scenariosAttempted: 0,
  scenariosCorrect: 0,
  scenariosByCategory: {
    'basic': { attempted: 0, correct: 0 },
    'soft-hands': { attempted: 0, correct: 0 },
    'splits': { attempted: 0, correct: 0 },
    'advanced': { attempted: 0, correct: 0 },
    'deviations': { attempted: 0, correct: 0 },
    'insurance': { attempted: 0, correct: 0 }
  },
  scenariosByDifficulty: {
    'easy': { attempted: 0, correct: 0 },
    'medium': { attempted: 0, correct: 0 },
    'hard': { attempted: 0, correct: 0 }
  },
  totalXP: 0,
  level: 1,
  drillsCompleted: 0
};

// Default speed drill state
export const DEFAULT_SPEED_DRILL: SpeedDrillState = {
  isActive: false,
  isPaused: false,
  timer: 60,
  duration: 60,
  speed: 1000,
  score: 0,
  highScore: 0,
  lastScore: null,
  isNewHighScore: false,
  isFinished: false,
  lastAttemptTime: null
};

// Build speed drill defaults with settings
export const buildSpeedDrillDefaults = (settings: GameSettings, highScore = 0): SpeedDrillState => ({
  ...DEFAULT_SPEED_DRILL,
  duration: settings.defaultSpeedDrillDuration ?? DEFAULT_SPEED_DRILL.duration,
  timer: settings.defaultSpeedDrillDuration ?? DEFAULT_SPEED_DRILL.duration,
  speed: settings.defaultSpeedDrillSpeed ?? DEFAULT_SPEED_DRILL.speed,
  highScore
});

// Default table configuration
export const DEFAULT_TABLE_CONFIG: TableConfiguration = {
  numberOfSeats: 7,
  activePlayers: 2,
  userHandsCount: 1,
  userSeatPositions: [3],
  dealSpeed: 500
};

// Default table state
export const DEFAULT_TABLE_STATE: TableState = {
  dealingPhase: 'idle',
  currentSeatIndex: -1,
  currentHandIndex: 0,
  isAutoPlaying: false,
  insuranceOffered: false,
  insuranceTaken: false
};

// Default count check state
export const DEFAULT_COUNT_CHECK: CountCheckState = {
  isActive: false,
  correctAnswer: 0,
  userAnswer: null,
  result: null
};

// XP reward amounts
export const XP_REWARDS = {
  handPlayed: 1,
  handWon: 2,
  blackjack: 5,
  perfectCount: 3,
  perfectBet: 2,
  drillCompleted: 5,
  achievementUnlocked: 25,
};

/**
 * Create initial seats based on table configuration
 */
export function createSeats(config: TableConfiguration): Seat[] {
  const seats: Seat[] = [];

  for (let i = 0; i < config.numberOfSeats; i++) {
    const isUserSeat = config.userSeatPositions.includes(i);
    const isAISeat = !isUserSeat && seats.filter(s => s.playerType === 'ai').length < config.activePlayers;

    seats.push({
      seatNumber: i,
      playerType: isUserSeat ? 'user' : isAISeat ? 'ai' : 'empty',
      hands: [{ cards: [], bet: 0 }],
      isActive: isUserSeat || isAISeat
    });
  }

  return seats;
}

